/**
 * Diccionario de ejercicios USAG WAG (Niveles 1-5) — MVP
 * Basado en framework biomecánico computable con MediaPipe Pose.
 *
 * Cada ejercicio define `checks[]`. Cada check se evalúa frame a frame
 * por el evaluator. Los umbrales se derivaron de la progresión técnica
 * USAG 2021-2029 y deben calibrarse con atletas reales.
 *
 * Tipos de check soportados:
 *   - 'angulo'          → angle(A,B,C) comparado con ideal/umbrales
 *   - 'linealidad'      → alineación de ≥3 puntos
 *   - 'distancia_norm'  → distancia entre dos puntos / altura del cuerpo
 *   - 'simetria_angulo' → diferencia angular entre lado izq y der
 *   - 'apertura_piernas'→ ángulo (L_ANKLE, hip_mid, R_ANKLE)
 */

export const EXERCISES = {
  // ========================== NIVEL 1 ==========================
  vertical_espalda_n1: {
    id: 'vertical_espalda_n1',
    level: 1,
    name: 'Vertical sobre espalda',
    description: 'Vertical estática invertida. Evalúa alineación y bloqueo.',
    fase: 'estatica',
    useApexDetector: false,
    checks: [
      {
        id: 'bloqueo_hombro',
        name: 'Bloqueo de hombro',
        type: 'angulo',
        points: ['wrist', 'shoulder', 'hip'],
        side: 'any',
        ideal: 180, warn: 172, critical: 160,
        direction: 'lower_is_worse',
        message: 'Hombros cerrados: abre más, empuja el piso.',
        drill: 'Hollow hold + shoulder shrugs contra pared.',
      },
      {
        id: 'alineacion_cuerpo',
        name: 'Alineación hombro-cadera-tobillo',
        type: 'linealidad',
        points: ['shoulder', 'hip', 'ankle'],
        side: 'any',
        warn: 0.04, critical: 0.07,
        message: 'Cuerpo no alineado: evita el arqueo lumbar (plátano).',
        drill: 'Hollow body hold 3×30s.',
      },
      {
        id: 'codos_rectos',
        name: 'Codos extendidos',
        type: 'angulo',
        points: ['shoulder', 'elbow', 'wrist'],
        side: 'any',
        ideal: 180, warn: 172, critical: 165,
        direction: 'lower_is_worse',
        message: 'Codos doblados: bloquéalos, firmes.',
      },
      {
        id: 'punta_pie',
        name: 'Punta de pie extendida',
        type: 'angulo',
        points: ['knee', 'ankle', 'foot_idx'],
        side: 'any',
        ideal: 180, warn: 165, critical: 150,
        direction: 'lower_is_worse',
        message: 'Pie flex: estira la punta hasta el techo.',
      },
    ],
  },

  split_frontal_n1: {
    id: 'split_frontal_n1',
    level: 1,
    name: 'Split frontal (Spagat)',
    description: 'Apertura de piernas en el suelo.',
    fase: 'estatica',
    useApexDetector: false,
    checks: [
      {
        id: 'apertura_180',
        name: 'Apertura total',
        type: 'apertura_piernas',
        ideal: 180, warn: 168, critical: 150,
        direction: 'lower_is_worse',
        message: 'Baja más la cadera, apertura completa al piso.',
      },
      {
        id: 'rodilla_L_recta',
        name: 'Rodilla izquierda recta',
        type: 'angulo',
        points: ['hip', 'knee', 'ankle'],
        side: 'L',
        ideal: 180, warn: 172, critical: 165,
        direction: 'lower_is_worse',
        message: 'Rodilla izquierda bien estirada.',
      },
      {
        id: 'rodilla_R_recta',
        name: 'Rodilla derecha recta',
        type: 'angulo',
        points: ['hip', 'knee', 'ankle'],
        side: 'R',
        ideal: 180, warn: 172, critical: 165,
        direction: 'lower_is_worse',
        message: 'Rodilla derecha bien estirada.',
      },
    ],
  },

  rueda_n1: {
    id: 'rueda_n1',
    level: 1,
    name: 'Rueda (Cartwheel)',
    description: 'Secuencia dinámica. Evalúa el paso por vertical.',
    fase: 'dinamica',
    useApexDetector: true,
    checks: [
      {
        id: 'apertura_vertical',
        name: 'Apertura al pasar por vertical',
        type: 'apertura_piernas',
        evaluateOnlyAtApex: true,
        ideal: 180, warn: 155, critical: 130,
        direction: 'lower_is_worse',
        message: 'Abre más las piernas al pasar, no brinques.',
      },
      {
        id: 'rodilla_L_recta',
        name: 'Rodilla izquierda extendida',
        type: 'angulo',
        points: ['hip', 'knee', 'ankle'],
        side: 'L',
        ideal: 180, warn: 170, critical: 160,
        direction: 'lower_is_worse',
        message: 'Rodillas duras, no las dobles.',
      },
      {
        id: 'rodilla_R_recta',
        name: 'Rodilla derecha extendida',
        type: 'angulo',
        points: ['hip', 'knee', 'ankle'],
        side: 'R',
        ideal: 180, warn: 170, critical: 160,
        direction: 'lower_is_worse',
        message: 'Rodillas duras, no las dobles.',
      },
    ],
  },

  // ========================== NIVEL 2 ==========================
  split_jump_90_n2: {
    id: 'split_jump_90_n2',
    level: 2,
    name: 'Salto Split 90°',
    description: 'Evaluado en el pico del salto (altura máxima).',
    fase: 'dinamica_pico',
    useApexDetector: true,
    checks: [
      {
        id: 'apertura_pico',
        name: 'Apertura en el pico',
        type: 'apertura_piernas',
        evaluateOnlyAtApex: true,
        ideal: 90, warn: 75, critical: 55,
        direction: 'lower_is_worse',
        message: 'Abre más en el aire, más tiempo arriba.',
      },
      {
        id: 'simetria_brazos',
        name: 'Simetría de brazos',
        type: 'simetria_angulo',
        points: ['shoulder', 'elbow', 'wrist'],
        toleranceDeg: 8, criticalDeg: 20,
        message: 'Brazos desparejos: ambos a la misma altura.',
      },
      {
        id: 'rodillas_vuelo',
        name: 'Rodillas extendidas en vuelo',
        type: 'angulo',
        points: ['hip', 'knee', 'ankle'],
        side: 'any',
        ideal: 180, warn: 172, critical: 165,
        direction: 'lower_is_worse',
        evaluateOnlyAtApex: true,
        message: 'Rodillas duras cuando saltas.',
      },
    ],
  },

  // ========================== NIVEL 3 ==========================
  rondada_n3: {
    id: 'rondada_n3',
    level: 3,
    name: 'Rondada (Round-off)',
    description: 'Secuencia dinámica con giro de 180°.',
    fase: 'dinamica',
    useApexDetector: false,
    checks: [
      {
        id: 'bloqueo_hombros',
        name: 'Bloqueo de hombros en apoyo',
        type: 'angulo',
        points: ['wrist', 'shoulder', 'hip'],
        side: 'any',
        ideal: 180, warn: 170, critical: 155,
        direction: 'lower_is_worse',
        message: 'Hombros empujando fuerte contra el piso.',
      },
      {
        id: 'piernas_juntas_final',
        name: 'Piernas juntas al aterrizar',
        type: 'distancia_norm',
        points: ['ankle_L', 'ankle_R'],
        warn: 0.06, critical: 0.12,
        direction: 'higher_is_worse',
        evaluateOnlyAtPhase: 'apoyo_pies',
        message: 'Junta las piernas al caer, no las abras.',
      },
      {
        id: 'cuerpo_recto',
        name: 'Cuerpo alineado en rechazo',
        type: 'linealidad',
        points: ['shoulder', 'hip', 'ankle'],
        side: 'any',
        warn: 0.06, critical: 0.10,
        message: 'Cuerpo recto al rechazar, no arquees.',
      },
    ],
  },

  // ========================== NIVEL 5 ==========================
  press_handstand_n5: {
    id: 'press_handstand_n5',
    level: 5,
    name: 'Press Handstand',
    description: 'Subida a vertical con fuerza, sin impulso.',
    fase: 'dinamica_lenta',
    useApexDetector: false,
    checks: [
      {
        id: 'vertical_final',
        name: 'Vertical final alineada',
        type: 'linealidad',
        points: ['shoulder', 'hip', 'ankle'],
        side: 'any',
        warn: 0.05, critical: 0.09,
        message: 'Cuerpo recto al llegar arriba, no arquees.',
      },
      {
        id: 'bloqueo_hombro_final',
        name: 'Bloqueo de hombro',
        type: 'angulo',
        points: ['wrist', 'shoulder', 'hip'],
        side: 'any',
        ideal: 180, warn: 170, critical: 158,
        direction: 'lower_is_worse',
        message: 'Hombros completamente abiertos en la posición final.',
      },
      {
        id: 'codos_firmes',
        name: 'Codos extendidos',
        type: 'angulo',
        points: ['shoulder', 'elbow', 'wrist'],
        side: 'any',
        ideal: 180, warn: 172, critical: 165,
        direction: 'lower_is_worse',
        message: 'Codos firmes todo el movimiento.',
      },
    ],
  },
};

export const EXERCISE_LIST = Object.values(EXERCISES).sort(
  (a, b) => a.level - b.level || a.name.localeCompare(b.name)
);

export function getExercise(id) {
  return EXERCISES[id] || null;
}
